import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  getLawCollegeAutocomplete,
  isExactLawCollege,
  searchLawColleges,
} from '../lib/pakistanLawColleges';
import { contextFieldInputClass } from './ui/ContextTypography';

export interface LawCollegeAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string;
  placeholder?: string;
  autoFocus?: boolean;
  variant?: 'onboarding' | 'context';
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
}

export default function LawCollegeAutocompleteInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  error,
  placeholder,
  autoFocus = false,
  variant = 'onboarding',
  className = '',
  inputClassName = '',
  disabled = false,
}: LawCollegeAutocompleteInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const userEditedRef = useRef(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [forceClose, setForceClose] = useState(false);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(
    () => variant === 'context' && Boolean(value?.trim()),
  );
  const listId = useId();

  const isOnboarding = variant === 'onboarding';
  const isContext = variant === 'context';

  useEffect(() => {
    if (isContext && !userEditedRef.current) {
      setSuggestionsDismissed(Boolean(value.trim()));
    }
  }, [value, isContext]);

  const exactMatch = useMemo(() => isExactLawCollege(value), [value]);

  const completion = useMemo(
    () => (exactMatch ? null : getLawCollegeAutocomplete(value)),
    [value, exactMatch],
  );

  const suggestions = useMemo(() => {
    if (exactMatch || !value.trim()) return [];
    if (isOnboarding && forceClose) return [];
    if (isContext && suggestionsDismissed) return [];
    return searchLawColleges(value, undefined, 12);
  }, [value, exactMatch, forceClose, suggestionsDismissed, isOnboarding, isContext]);

  const showList = suggestions.length > 0;

  const selectCollege = (college: string) => {
    onChange(college);
    setHighlightedIndex(0);
    if (isOnboarding) {
      setForceClose(true);
      inputRef.current?.blur();
    } else {
      setSuggestionsDismissed(true);
      userEditedRef.current = false;
      inputRef.current?.focus();
    }
  };

  const acceptCompletion = () => {
    if (!completion) return;
    selectCollege(completion.full);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isContext) {
      if (e.key === 'Backspace') {
        setSuggestionsDismissed(false);
      } else if (suggestionsDismissed) {
        onKeyDown?.(e);
        return;
      }
    }

    if (completion && (e.key === 'ArrowRight' || e.key === 'Tab')) {
      const input = inputRef.current;
      const atEnd =
        !input ||
        (input.selectionStart === input.selectionEnd &&
          input.selectionEnd === value.length);

      if (atEnd) {
        e.preventDefault();
        acceptCompletion();
        return;
      }
    }

    if (e.key === 'ArrowDown' && suggestions.length > 0) {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
      return;
    }

    if (e.key === 'ArrowUp' && suggestions.length > 0) {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
      return;
    }

    if (e.key === 'Enter' && suggestions.length > 0 && suggestions[highlightedIndex]) {
      e.preventDefault();
      selectCollege(suggestions[highlightedIndex]);
      return;
    }

    if (e.key === 'Escape' && showList) {
      e.preventDefault();
      if (isOnboarding) setForceClose(true);
      else setSuggestionsDismissed(true);
      return;
    }

    onKeyDown?.(e);
  };

  const defaultInputClass = isOnboarding
    ? `w-full text-2xl md:text-4xl bg-transparent border-b-2 py-4 outline-none placeholder:text-white/20 font-medium transition-colors ${
        error ? 'border-red-400/80 focus:border-red-400' : 'border-white/20 focus:border-white'
      }`
    : `${contextFieldInputClass} ${error ? 'border-red-400' : ''}`;

  const inputClass = `${defaultInputClass} ${inputClassName}`.trim();

  const ghostClass = isOnboarding
    ? 'text-2xl md:text-4xl font-medium text-white/25 pointer-events-none select-none whitespace-pre'
    : 'text-base text-slate-400 pointer-events-none select-none whitespace-pre';

  const listClass = isOnboarding
    ? 'mt-2 max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-[#1E293B]/95 shadow-lg custom-scrollbar'
    : 'mt-1 max-h-48 overflow-y-auto rounded-xl border border-border-light bg-white shadow-lg z-30 relative';

  const itemClass = (active: boolean) =>
    isOnboarding
      ? `w-full px-4 py-2.5 text-left text-sm transition-colors ${
          active ? 'bg-blue-500/20 text-white' : 'text-slate-300 hover:bg-white/5'
        }`
      : `w-full px-4 py-2 text-left text-sm transition-colors ${
          active ? 'bg-primary/10 text-primary' : 'text-[#0F172A] hover:bg-slate-50'
        }`;

  const hintClass = isOnboarding ? 'text-slate-500 text-xs mt-2' : 'text-slate-400 text-xs mt-1';
  const errorClass = isOnboarding ? 'text-red-400 text-sm mt-2' : 'text-red-600 text-sm mt-1';
  const ghostPadding = isOnboarding ? 'py-4' : 'px-3 py-2.5';

  return (
    <div className={`w-full ${className}`.trim()}>
      <div className="relative">
        <div
          className={`absolute inset-0 flex items-center overflow-hidden pointer-events-none ${ghostPadding}`}
          aria-hidden
        >
          <span className={`${ghostClass} invisible`}>{value}</span>
          {completion?.suffix ? <span className={ghostClass}>{completion.suffix}</span> : null}
        </div>
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          autoFocus={autoFocus}
          disabled={disabled}
          data-onboarding-focus={isOnboarding ? true : undefined}
          className={`${inputClass} relative z-[1] ${
            isOnboarding ? 'bg-transparent' : ''
          } disabled:cursor-not-allowed disabled:opacity-60`}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            if (isOnboarding) setForceClose(false);
            else {
              userEditedRef.current = true;
              setSuggestionsDismissed(false);
            }
            onChange(e.target.value);
            setHighlightedIndex(0);
          }}
          onFocus={() => {
            if (exactMatch) return;
            if (isOnboarding) setForceClose(false);
            else {
              userEditedRef.current = true;
              setSuggestionsDismissed(false);
            }
          }}
          onBlur={(e) => onBlur?.(e.target.value)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listId}
        />
      </div>

      {completion && (
        <p className={hintClass}>
          {completion.suffix
            ? 'Press → or Tab to complete'
            : 'Press → or Tab to select matched college'}
        </p>
      )}

      {showList && (
        <ul id={listId} role="listbox" className={listClass}>
          {suggestions.map((college, idx) => (
            <li key={college} role="option" aria-selected={idx === highlightedIndex}>
              <button
                type="button"
                className={itemClass(idx === highlightedIndex)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectCollege(college)}
              >
                {college}
              </button>
            </li>
          ))}
        </ul>
      )}

      {!showList && value.trim() && !exactMatch && isOnboarding && (
        <p className={hintClass}>
          No match — try the school name, city, or short form (e.g. ISL, Blackstone, TMUC).
        </p>
      )}

      {error && <p className={errorClass}>{error}</p>}
    </div>
  );
}
